import { useMemo, useState } from 'react'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'
import { getArrayVariable, getTextVariable } from './template-variable-utils'

interface MedicationOption {
  id: string
  label: string
}

interface WhichMedicationsTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  currentPhase?: PhaseInfo | null
  onSelect: (value: AnswerValue) => void
}

export default function WhichMedicationsTemplate({
  stage,
  answers,
  currentPhase,
  onSelect,
}: WhichMedicationsTemplateProps) {
  const header = getTextVariable(stage.templateVariables, 'header', 'Got it — which ones have you tried?')
  const subHeader = getTextVariable(
    stage.templateVariables,
    'subHeader',
    "This helps me understand what's worked for you and what hasn't."
  )
  const options = getArrayVariable<MedicationOption>(stage.templateVariables, 'options', [])

  const initialSelection = useMemo(() => {
    if (Array.isArray(answers.current_glp1_medications)) {
      return (answers.current_glp1_medications as string[]).filter(Boolean)
    }
    if (typeof answers.current_medication === 'string' && answers.current_medication) {
      return [answers.current_medication as string]
    }
    return []
  }, [answers.current_glp1_medications, answers.current_medication])

  const [selected, setSelected] = useState<string[]>(initialSelection)

  const toggle = (value: string) => {
    setSelected((prev) => {
      if (value === 'other') {
        return prev.includes(value) ? [] : ['other']
      }
      const withoutOther = prev.filter((item) => item !== 'other')
      return withoutOther.includes(value)
        ? withoutOther.filter((item) => item !== value)
        : [...withoutOther, value]
    })
  }

  const handleContinue = () => {
    if (!selected.length) return
    onSelect({
      current_glp1_medications: selected,
      current_medication: selected[0],
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="space-y-2">
        {options.map((option) => {
          const isSelected = selected.includes(option.id)
          return (
            <button
              key={option.id}
              type="button"
              className={`flex w-full items-center gap-3.5 rounded-2xl border px-[18px] py-4 text-left transition-all duration-300 ${
                isSelected
                  ? 'border-[var(--ww-accent)] choice-selected selected-glow'
                  : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'
              }`}
              onClick={() => toggle(option.id)}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-[1.5px] ${
                  isSelected ? 'border-[var(--ww-accent)] bg-[var(--ww-accent)] text-[#0a0a0a]' : 'border-[var(--ww-text-3)]'
                }`}
              >
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <span className="text-[15px] font-medium text-white">{option.label}</span>
            </button>
          )
        })}
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          type="button"
          disabled={selected.length === 0}
          onClick={handleContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
